import {
  WorkflowStep,
  StepStatus,
  WorkflowStepStatus,
  CRM_IMPORT_BASE_ROUTE,
} from "../constants";

/**
 * Helper function to get route path for a workflow step
 * @param step - The workflow step
 * @param queryParams - Optional query parameters to append to the URL
 */
export const getWorkflowRoute = (
  step: WorkflowStep,
  queryParams?: Record<string, string>,
): string => {
  const basePath = `${CRM_IMPORT_BASE_ROUTE}/${step}`;

  if (!queryParams || Object.keys(queryParams).length === 0) {
    return basePath;
  }

  const searchParams = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

/**
 * Extracts the current workflow step from the URL path
 * @param pathname - The current location pathname
 * @returns The current workflow step or PROJECT_SETUP as fallback
 */
export const getCurrentStepFromPath = (pathname: string): WorkflowStep => {
  // Extract the step from the path by removing the base route
  const stepFromPath = pathname.replace(`${CRM_IMPORT_BASE_ROUTE}/`, "");

  // Check if it matches any of our workflow steps
  for (const step of Object.values(WorkflowStep)) {
    if (stepFromPath === step) {
      return step;
    }
  }
  return WorkflowStep.PROJECT_SETUP; // Default fallback
};

/**
 * Gets the status of a workflow step relative to the current step
 * @param step - The step to check status for
 * @param currentStep - The current active step
 * @returns The status of the step
 */
export const getStepStatus = (
  step: WorkflowStep,
  currentStep: WorkflowStep,
): WorkflowStepStatus => {
  const stepOrder = [
    WorkflowStep.PROJECT_SETUP,
    WorkflowStep.DATA_IMPORT,
    WorkflowStep.DATA_VALIDATION,
  ];

  const currentIndex = stepOrder.indexOf(currentStep);
  const stepIndex = stepOrder.indexOf(step);

  if (stepIndex < currentIndex) return WorkflowStepStatus.COMPLETED;
  if (stepIndex === currentIndex) return WorkflowStepStatus.CURRENT;
  return WorkflowStepStatus.UPCOMING;
};

/**
 * Gets the status of a workflow step for ImportProgress component
 * @param step - The step to check status for
 * @param currentStep - The current active step
 * @param stepOrder - The order of steps for this workflow
 * @returns The status of the step using StepStatus enum
 */
export const getImportProgressStepStatus = (
  step: WorkflowStep,
  currentStep: WorkflowStep,
  stepOrder: WorkflowStep[],
): StepStatus => {
  const currentIndex = stepOrder.indexOf(currentStep);
  const stepIndex = stepOrder.indexOf(step);

  if (stepIndex < currentIndex) return StepStatus.COMPLETED;
  if (stepIndex === currentIndex) return StepStatus.ACTIVE;
  return StepStatus.PENDING;
};
